import { useCallback, useMemo, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { AmlTraceHistoryItemData } from '@/redux/models';
import { TraceTypeEnum } from '@/config';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { amlTraceSelectors } from '@/redux/selectors';
import { amlTraceActions } from '@/redux/actions';
import { useDevice } from '@/hooks';
import { TraceHistoryEntry } from '../TraceHistoryEntry/TraceHistoryEntry';
import {
  TraceHistoryMessage,
  TraceHistoryMessages,
} from '../TraceHistoryMessages/TraceHistoryMessages';

import styles from './TraceHistoryItem.module.sass';

interface TraceHistoryItemProps {
  item: AmlTraceHistoryItemData;
  traceUpdates: any[];
}

const TraceHistoryItem = ({
  item,
  traceUpdates,
}: TraceHistoryItemProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { isMobile } = useDevice();
  const activeUuid = useAppSelector(amlTraceSelectors.getActiveUuid);
  const [isManualExpanded, setIsManualExpanded] = useState(false);
  const isExpanded = isManualExpanded || activeUuid === item.uuid;

  const handleToggleEntry = useCallback(() => {
    if (isExpanded) {
      if (activeUuid === item.uuid) {
        dispatch(amlTraceActions.setActiveUuid(null));
      }
      setIsManualExpanded(() => false);
    } else {
      setIsManualExpanded(() => true);
    }
  }, [dispatch, activeUuid, item.uuid, isExpanded]);

  const visualizationUrl = useMemo(() => {
    if (item.info?.visualizationUrl) {
      return item.info.visualizationUrl;
    }

    if (traceUpdates?.length) {
      const visualization = traceUpdates.find(
        (update) => update?.visualizationUrl,
      );
      return visualization?.visualizationUrl;
    }

    return null;
  }, [item.info, traceUpdates]);

  const statusMessages = useMemo((): TraceHistoryMessage[] => {
    if (!traceUpdates?.length) return [];

    return traceUpdates
      .filter(
        (update) => update?.type === TraceTypeEnum.STATUS && update?.message,
      )
      .map((update, idx) => ({
        idx: idx,
        message: update.message,
      }));
  }, [traceUpdates]);

  const errorMessages = useMemo((): TraceHistoryMessage[] => {
    if (!traceUpdates?.length) return [];

    return traceUpdates
      .filter(
        (update) => update?.type === TraceTypeEnum.ERROR && update?.message,
      )
      .map((update, idx) => ({
        idx: idx,
        message: update.message,
      }));
  }, [traceUpdates]);

  const hasUpdates = statusMessages.length > 0;
  const hasErrors = errorMessages.length > 0;
  const hasVisualization = !!visualizationUrl;

  const handleExpand = useCallback(() => {
    if (isMobile && visualizationUrl) {
      window.open(visualizationUrl, '_blank');
    } else {
      handleToggleEntry();
    }
  }, [isMobile, visualizationUrl, handleToggleEntry]);

  return (
    <div className={styles.item}>
      <TraceHistoryEntry
        isExpanded={isExpanded}
        item={item}
        onExpand={handleExpand}
      />

      <AnimateHeight duration={300} height={isExpanded ? 'auto' : 0}>
        <div className={styles.details}>
          {hasUpdates && !hasErrors && !hasVisualization && (
            <TraceHistoryMessages messages={statusMessages} />
          )}

          {hasErrors && !hasVisualization && (
            <TraceHistoryMessages messages={errorMessages} />
          )}

          {hasVisualization && (
            <div className={styles.visualization}>
              <iframe
                src={visualizationUrl}
                title={`Trace visualization ${item.uuid}`}
                allow="clipboard-read; clipboard-write"
                loading="lazy"
                sandbox="allow-scripts allow-same-origin allow-popups allow-modals allow-forms allow-downloads"
              />
            </div>
          )}
        </div>
      </AnimateHeight>
    </div>
  );
};

export default TraceHistoryItem;
